/** @jsxImportSource @emotion/react */
import { useState } from "react";
import { MenuBarMenu } from "components/MenuBar/MenuBarMenu";
import { MenuBar } from "components/MenuBar/MenuBar";
import { ModalCreateRows } from "../modals/ModalCreateRows";
import { ModalCreateColumn } from "../modals/ModalCreateColumn/ModalCreateColumn";
import { useDataTableContext } from "../store/DataTableProvider";
import { ModalDeleteRows } from "../modals/ModalDeleteRows";
import { ModalDeleteTable } from "../modals/ModalDeleteTable";
import { isDefined } from "utils/isDefined";
import { ModalDandiGdt } from "components/ModalDandi/ModalDandiGdt";
import { EuiFlexGroup, EuiFlexItem } from "@inscopix/ideas-eui";
import { DataTableSyncStatusBadge } from "./DataTableSyncStatusBadge";
import { ModalEditDataTable } from "../modals/ModalEditDataTable";
import { ModalEditAnalysisTable } from "../modals/ModalEditAnalysisTable";
import { ModalEditRowIdentifierFormat } from "../modals/ModalEditRowIdentifierFormat";
import { ModalCreateColumnFromExistingTable } from "../modals/ModalCreateColumn/ModalCreateColumnFromExistingTable";
import { ModalCreateAnalysisTable } from "../modals/ModalCreateColumn/ModalCreateAnalysisTable";

type Menu = "file" | "edit" | "view" | "insert";

type Modal =
  | "create_column"
  | "create_rows"
  | "delete_rows"
  | "delete_table"
  | "dandi"
  | "edit_data_table"
  | "edit_analysis_table"
  | "identifier"
  | "join_table"
  | "insert_new_analysis";

/**
 * Component that renders a menu bar for triggering actions on a data table or
 * analysis table
 */
export const DataTableMenuBar = () => {
  const selectedTable = useDataTableContext((s) => {
    return s.tables.find(({ id }) => id === s.selectedTableId);
  });
  const setFlyoutRightContent = useDataTableContext(
    (s) => s.setFlyoutRightContent,
  );
  const setFlyoutBottomContent = useDataTableContext(
    (s) => s.setFlyoutBottomContent,
  );
  const selectedRowIds = useDataTableContext((s) => s.selectedRowIds);
  const isAdvancedMode = useDataTableContext((s) => s.isAdvancedMode);
  const setIsAdvancedMode = useDataTableContext((s) => s.setIsAdvancedMode);
  const downloadTableAsCsv = useDataTableContext((s) => s.downloadTableAsCsv);
  const [selectedMenu, setSelectedMenu] = useState<Menu>();
  const [visibleModal, setVisibleModal] = useState<Modal>();
  const isAnyMenuOpen = selectedMenu !== undefined;
  const closeMenu = () => setSelectedMenu(undefined);

  const onClose = () => {
    setVisibleModal(undefined);
  };

  // must find selected table to render
  if (!isDefined(selectedTable)) {
    return null;
  }

  return (
    <>
      {visibleModal === "create_column" && (
        <ModalCreateColumn onClose={onClose} />
      )}
      {visibleModal === "create_rows" && <ModalCreateRows onClose={onClose} />}
      {visibleModal === "delete_rows" && (
        <ModalDeleteRows
          tableId={selectedTable.id}
          rowIds={selectedRowIds}
          onClose={onClose}
        />
      )}
      {visibleModal === "delete_table" && (
        <ModalDeleteTable tableId={selectedTable.id} onClose={onClose} />
      )}
      {visibleModal === "dandi" && <ModalDandiGdt onClose={onClose} />}
      {visibleModal === "edit_data_table" && (
        <ModalEditDataTable table={selectedTable} onClose={onClose} />
      )}
      {visibleModal === "edit_analysis_table" && (
        <ModalEditAnalysisTable table={selectedTable} onClose={onClose} />
      )}
      {visibleModal === "identifier" && (
        <ModalEditRowIdentifierFormat onClose={onClose} />
      )}
      {visibleModal === "join_table" && (
        <ModalCreateColumnFromExistingTable onClose={onClose} />
      )}
      {visibleModal === "insert_new_analysis" && (
        <ModalCreateAnalysisTable
          dataTableId={selectedTable.id}
          folderId={selectedTable.folder}
          onClose={onClose}
        />
      )}
      <EuiFlexGroup gutterSize="none" responsive={false} alignItems="center">
        <EuiFlexItem>
          <MenuBar>
            <MenuBarMenu
              label="File"
              enableHoverToOpen={isAnyMenuOpen}
              isOpen={selectedMenu === "file"}
              items={[
                {
                  type: "item",
                  icon: "gear",
                  label: "Edit table",
                  onClick: () => {
                    if (selectedTable?.kind === "data") {
                      setVisibleModal("edit_data_table");
                    } else if (selectedTable?.kind === "analysis") {
                      setVisibleModal("edit_analysis_table");
                    }
                  },
                  requiredPermission: "edit",
                },
                {
                  type: "item",
                  icon: "trash",
                  label: "Delete table",
                  onClick: () => setVisibleModal("delete_table"),
                  disabled: selectedTable === undefined,
                  requiredPermission: "edit",
                },
                {
                  key: "separator_1",
                  type: "separator",
                },
                {
                  type: "item",
                  icon: "download",
                  label: "Download as CSV",
                  onClick: () => {
                    if (isDefined(selectedTable)) {
                      downloadTableAsCsv({
                        tableId: selectedTable.id,
                      });
                    }
                  },
                  disabled: selectedTable === undefined,
                  requiredPermission: "view",
                },
                {
                  type: "item",
                  icon: "exportAction",
                  label: "Export to DANDI",
                  onClick: () => setVisibleModal("dandi"),
                  disabled: selectedTable === undefined,
                  requiredPermission: "execute",
                  limitToType: "data",
                },
              ]}
              onClose={closeMenu}
              onOpen={() => setSelectedMenu("file")}
              tableType={selectedTable?.kind}
            />
            <MenuBarMenu
              label="Edit"
              enableHoverToOpen={isAnyMenuOpen}
              isOpen={selectedMenu === "edit"}
              items={[
                {
                  type: "item",
                  icon: "trash",
                  label: "Delete selected rows",
                  onClick: () => setVisibleModal("delete_rows"),
                  disabled: selectedRowIds.length === 0,
                  requiredPermission: "edit",
                },
                {
                  key: "separator_1",
                  type: "separator",
                },
                {
                  type: "item",
                  icon: "nested",
                  label: "Edit row identifiers",
                  onClick: () => setVisibleModal("identifier"),
                  requiredPermission: "edit",
                },
                {
                  key: "separator_2",
                  type: "separator",
                },
                {
                  type: "item",
                  label: isAdvancedMode
                    ? "Disable advanced mode"
                    : "Enable advanced mode",
                  icon: "beta",
                  onClick: () => {
                    if (isAdvancedMode) {
                      setIsAdvancedMode(false);
                    } else {
                      setIsAdvancedMode(true);
                    }
                  },
                  requiredPermission: "edit",
                },
              ]}
              onClose={closeMenu}
              onOpen={() => setSelectedMenu("edit")}
              tableType={selectedTable?.kind}
            />
            <MenuBarMenu
              label="View"
              enableHoverToOpen={isAnyMenuOpen}
              isOpen={selectedMenu === "view"}
              items={[
                {
                  type: "item",
                  icon: "folderOpen",
                  label: "Files",
                  onClick: () => setFlyoutBottomContent("fileBrowser"),
                  requiredPermission: "view",
                },
                {
                  type: "item",
                  icon: "notebookApp",
                  label: "Notes",
                  onClick: () => setFlyoutRightContent("tableDescription"),
                  requiredPermission: "view",
                },
              ]}
              onClose={closeMenu}
              onOpen={() => setSelectedMenu("view")}
              tableType={selectedTable?.kind}
            />
            <MenuBarMenu
              label="Insert"
              enableHoverToOpen={isAnyMenuOpen}
              isOpen={selectedMenu === "insert"}
              items={[
                {
                  type: "item",
                  icon: "plus",
                  label: "Insert row(s)",
                  onClick: () => setVisibleModal("create_rows"),
                  disabled: selectedTable === undefined,
                  requiredPermission: "edit",
                },
                {
                  type: "item",
                  icon: "plus",
                  label: "Insert column(s)",
                  onClick: () => setVisibleModal("create_column"),
                  disabled:
                    selectedTable === undefined ||
                    selectedTable.kind !== "data",
                  requiredPermission: "edit",
                  limitToType: "data",
                },
                {
                  type: "item",
                  icon: "plus",
                  label: "Insert new analysis",
                  onClick: () => setVisibleModal("insert_new_analysis"),
                  disabled:
                    selectedTable === undefined ||
                    selectedTable.kind !== "data",
                  requiredPermission: "edit",
                  limitToType: "data",
                },
                {
                  type: "item",
                  icon: "link",
                  label: "Insert columns from another table",
                  onClick: () => setVisibleModal("join_table"),
                  disabled:
                    selectedTable === undefined ||
                    selectedTable.kind !== "data",
                  requiredPermission: "edit",
                  limitToType: "data",
                },
              ]}
              onClose={closeMenu}
              onOpen={() => setSelectedMenu("insert")}
              tableType={selectedTable?.kind}
            />
          </MenuBar>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <DataTableSyncStatusBadge />
        </EuiFlexItem>
      </EuiFlexGroup>
    </>
  );
};
